import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import Keycloak from 'keycloak-js';
import { keycloak } from 'services';

// ─── Tipos ────────────────────────────────────────────────────────────────────

type KeycloakContextValue = {
  keycloak: Keycloak;
  initialized: boolean;
};

// ─── Context ──────────────────────────────────────────────────────────────────

const KeycloakContext = createContext<KeycloakContextValue | undefined>(
  undefined
);

// Flag de módulo para evitar dupla inicialização do Keycloak em StrictMode
// (o objeto `keycloak` é um singleton importado, então o controle precisa
// sobreviver a remontagens do Provider, e não apenas a um único componente).
let keycloakInitialized = false;

// ─── Hook (substitui useKeycloak do @react-keycloak/web) ──────────────────────

export const useKeycloak = (): KeycloakContextValue => {
  const ctx = useContext(KeycloakContext);
  if (!ctx) {
    throw new Error('useKeycloak deve ser usado dentro do KeycloakProvider');
  }
  return ctx;
};

// ─── Provider (substitui ReactKeycloakProvider) ───────────────────────────────

type KeycloakProviderProps = {
  children: React.ReactNode;
};

export const KeycloakProvider: React.FC<KeycloakProviderProps> = ({
  children,
}) => {
  const [initialized, setInitialized] = useState(false);

  useEffect(() => {
    // Evita dupla inicialização em StrictMode
    if (keycloakInitialized) {
      setInitialized(true);
      return;
    }

    keycloak
      .init({
        onLoad: 'login-required',
        pkceMethod: 'S256',
        checkLoginIframe: false,
      })
      .then(() => {
        keycloakInitialized = true;
        setInitialized(true);
      })
      .catch(err => {
        console.error('Keycloak init error:', err);
        setInitialized(true); // libera a app mesmo em erro para não travar
      });

    // Renovação automática do token 60s antes de expirar
    keycloak.onTokenExpired = (): void => {
      keycloak.updateToken(60).catch(() => {
        console.error('Falha ao renovar token — redirecionando para login');
        keycloak.login();
      });
    };
  }, []);

  const value = useMemo<KeycloakContextValue>(
    () => ({ keycloak, initialized }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [initialized, keycloak.authenticated]
  );

  if (!initialized) {
    return null; // ou um <Spinner /> enquanto o Keycloak carrega
  }

  return (
    <KeycloakContext.Provider value={value}>
      {children}
    </KeycloakContext.Provider>
  );
};
